import { v4 as uuidv4 } from "uuid";
import {
  ContentItem,
  ContentType,
  TextContent,
  ImageContent,
  VideoContent,
  AudioContent,
  DocumentContent,
  MultipleChoiceOptionContent,
} from "@org/ui";
import { ExerciseType } from "@org/model";
import { unwrapTrpcData } from "@org/ui";
import { exerciseRegistry } from "@org/exercises";

// --- Types ---

export type RawContentItem = {
  content: {
    contentId: string;
    contentType: string;
    title?: string | null;
    description?: string | null;
  };
  order?: number | null;
};

export type ContentDefinitionRequest = {
  contentId: string;
  contentType: string;
};

type BaseContent = {
  id: string;
  name: string;
  order: number;
  data: {
    title: string;
    description: string;
  };
};

type ContentHydratorArgs = {
  base: BaseContent;
  definition: any;
  item: RawContentItem;
};

type ContentHydrator = (args: ContentHydratorArgs) => ContentItem | null;

type ExerciseContentDefinition = {
  exerciseId?: string;
  exerciseType?: string;
  correction?: string | null;
  exerciseDetails?: any;
};

type ExerciseHydratorArgs = {
  base: BaseContent;
  definition: ExerciseContentDefinition;
};

type ExerciseHydrator = (args: ExerciseHydratorArgs) => ContentItem;

// --- Utilities ---

const DEFAULT_TEXT_FORMAT: TextContent["data"]["format"] = "plain";

export const unwrap = <T,>(value: T): any => {
  if (!value) return value;
  const unwrapped = unwrapTrpcData(value);
  return unwrapped ?? value;
};

const createBaseContent = (item: RawContentItem): BaseContent | null => {
  const { content } = item;
  if (!content?.contentId) return null;

  return {
    id: content.contentId,
    name: content.title || "Untitled",
    order: item.order ?? 0,
    data: {
      title: content.title || "",
      description: content.description || "",
    },
  };
};

export const extractFirstRecord = (value: unknown): any => {
  const data = unwrap(value);
  if (Array.isArray(data)) {
    return data[0] ?? null;
  }
  return data ?? null;
};

// --- Media Content Hydrators ---

const hydrateTextContent: ContentHydrator = ({ base, definition }) => ({
  ...base,
  type: ContentType.Text,
  data: {
    ...base.data,
    text: definition?.text ?? "",
    format: (definition?.format as TextContent["data"]["format"]) ?? DEFAULT_TEXT_FORMAT,
  },
}) as TextContent;

const hydrateImageContent: ContentHydrator = ({ base, definition }) => ({
  ...base,
  type: ContentType.Image,
  data: { ...base.data, url: definition?.url ?? "", ext: definition?.ext },
}) as ImageContent;

const hydrateVideoContent: ContentHydrator = ({ base, definition }) => ({
  ...base,
  type: ContentType.Video,
  data: { ...base.data, url: definition?.url ?? "", ext: definition?.ext },
}) as VideoContent;

const hydrateAudioContent: ContentHydrator = ({ base, definition }) => ({
  ...base,
  type: ContentType.Audio,
  data: { ...base.data, url: definition?.url ?? "" },
}) as AudioContent;

const hydrateDocumentContent: ContentHydrator = ({ base, definition }) => ({
  ...base,
  type: ContentType.Document,
  data: { ...base.data, url: definition?.url ?? "" },
}) as DocumentContent;

// --- Exercise Hydrators ---

const createExerciseBase = (base: BaseContent, definition: ExerciseContentDefinition) => ({
  ...base.data,
  exerciseId: definition?.exerciseId,
  type: (definition?.exerciseType as ExerciseType) ?? ExerciseType.MultipleChoice,
  correction: definition?.correction ?? "",
});

const hydrateMultipleChoiceExercise: ExerciseHydrator = ({ base, definition }) => {
  const details = definition.exerciseDetails ?? {};
  const rawOptions = Array.isArray(details.options) ? details.options : [];
  const fallbackCorrectIndex =
    typeof details.correctOptionIndex === "number"
      ? Math.max(0, Math.min(details.correctOptionIndex, Math.max(rawOptions.length - 1, 0)))
      : 0;

  const seededOptions = rawOptions.length
    ? rawOptions
    : [
        { optionId: uuidv4(), label: "", isCorrect: true, order: 0 },
        { optionId: uuidv4(), label: "", isCorrect: false, order: 1 },
      ];

  let normalizedOptions: MultipleChoiceOptionContent[] = seededOptions
    .map((option: any, index: number): MultipleChoiceOptionContent => {
      if (typeof option === "string") {
        return {
          optionId: uuidv4(),
          label: option,
          isCorrect: fallbackCorrectIndex === index,
          order: index,
        };
      }

      return {
        optionId: option?.optionId ?? option?.multipleChoiceOptionId ?? option?.id ?? uuidv4(),
        label: option?.label ?? "",
        isCorrect: typeof option?.isCorrect === "boolean" ? option.isCorrect : fallbackCorrectIndex === index,
        order: typeof option?.order === "number" ? option.order : index,
      };
    })
    .sort((a: MultipleChoiceOptionContent, b: MultipleChoiceOptionContent) => (a.order ?? 0) - (b.order ?? 0))
    .map((option: MultipleChoiceOptionContent, index: number) => ({
      ...option,
      order: index,
      isCorrect: !!option.isCorrect,
    }));

  if (!normalizedOptions.some((option) => option.isCorrect) && normalizedOptions.length) {
    normalizedOptions = normalizedOptions.map((option, index) => ({
      ...option,
      isCorrect: index === 0,
    }));
  }

  return {
    ...base,
    type: ContentType.Exercise,
    data: {
      ...createExerciseBase(base, definition),
      exerciseSubtype: ExerciseType.MultipleChoice,
      question: details.question ?? "",
      options: normalizedOptions,
    },
  } as ContentItem;
};

const hydrateWrittenExercise: ExerciseHydrator = ({ base, definition }) => {
  const details = definition.exerciseDetails ?? {};
  const textType = details.textType === "long" ? ExerciseType.LongAnswer : ExerciseType.ShortAnswer;

  return {
    ...base,
    type: ContentType.Exercise,
    data: {
      ...createExerciseBase(base, definition),
      exerciseSubtype: textType,
      format: details.format ?? "plain",
      textType: details.textType ?? "short",
    },
  } as ContentItem;
};

const hydrateRecordedExercise: ExerciseHydrator = ({ base, definition }) => {
  const details = definition.exerciseDetails ?? {};

  return {
    ...base,
    type: ContentType.Exercise,
    data: {
      ...createExerciseBase(base, definition),
      exerciseSubtype: ExerciseType.Recorded,
      maxDurationSec: details.maxDurationSec ?? 60,
    },
  } as ContentItem;
};

const hydrateFillInBlankExercise: ExerciseHydrator = ({ base, definition }) => {
  const details = definition.exerciseDetails ?? {};
  const sentences = Array.isArray(details.sentences) ? details.sentences : [];

  return {
    ...base,
    type: ContentType.Exercise,
    data: {
      ...createExerciseBase(base, definition),
      exerciseSubtype: ExerciseType.FillInBlank,
      sentences: sentences.map((sentence: any, index: number) => {
        const rawBlanks =
          Array.isArray(sentence?.blanks) && sentence.blanks.length
            ? sentence.blanks
            : typeof sentence?.answer === "string"
              ? [
                  {
                    answer: sentence.answer,
                    correction: sentence.correction ?? "",
                    position: 0,
                  },
                ]
              : [];

        return {
          sentenceId: sentence?.sentenceId,
          sentence: sentence?.sentence ?? "___",
          order: sentence?.order ?? index,
          correction: sentence?.correction ?? "",
          blanks: rawBlanks.map((blank: any, blankIndex: number) => ({
            blankId: blank?.blankId,
            position: blank?.position ?? blankIndex,
            answer: blank?.answer ?? "",
            correction: blank?.correction ?? "",
          })),
        };
      }),
    },
  } as ContentItem;
};

const hydrateSelectForBlankExercise: ExerciseHydrator = ({ base, definition }) => {
  const details = definition.exerciseDetails ?? {};
  const sentences = Array.isArray(details.sentences) ? details.sentences : [];

  return {
    ...base,
    type: ContentType.Exercise,
    data: {
      ...createExerciseBase(base, definition),
      exerciseSubtype: ExerciseType.SelectForBlank,
      sentences: sentences.map((sentence: any, sentenceIndex: number) => ({
        sentenceId: sentence?.sentenceId,
        sentence: sentence?.sentence ?? "___",
        order: sentence?.order ?? sentenceIndex,
        correction: sentence?.correction ?? "",
        blanks: (Array.isArray(sentence?.blanks) ? sentence?.blanks : []).map((blank: any, blankIndex: number) => ({
          blankId: blank?.blankId,
          position: blank?.position ?? blankIndex,
          options: (Array.isArray(blank?.options) ? blank?.options : [
            { optionId: undefined, label: "", isCorrect: true, order: 0 },
            { optionId: undefined, label: "", isCorrect: false, order: 1 },
          ]).map((option: any, optionIndex: number) => ({
            optionId: option?.optionId,
            label: option?.label ?? "",
            isCorrect: !!option?.isCorrect,
            order: option?.order ?? optionIndex,
          })),
        })),
      })),
    },
  } as ContentItem;
};

const hydrateFallbackExercise: ExerciseHydrator = ({ base, definition }) => ({
  ...base,
  type: ContentType.Exercise,
  data: {
    ...createExerciseBase(base, definition),
    exerciseSubtype: ExerciseType.MultipleChoice,
    question: "",
    options: [
      { optionId: uuidv4(), label: "", isCorrect: true, order: 0 },
      { optionId: uuidv4(), label: "", isCorrect: false, order: 1 },
    ],
  },
}) as ContentItem;

const hydrateConversationalExercise: ExerciseHydrator = ({ base, definition }) => {
  const details = definition.exerciseDetails ?? {};

  let targetSkills: string[] = [];
  if (Array.isArray(details.targetSkills)) {
    targetSkills = details.targetSkills;
  } else if (typeof details.targetSkills === "string") {
    try {
      targetSkills = JSON.parse(details.targetSkills);
    } catch {
      targetSkills = [];
    }
  }

  return {
    ...base,
    type: ContentType.Exercise,
    data: {
      ...createExerciseBase(base, definition),
      exerciseSubtype: ExerciseType.Conversational,
      theme: details.theme ?? "",
      maxDurationSec: details.maxDurationSec ?? 300,
      systemPrompt: details.systemPrompt ?? "",
      targetSkills,
    },
  } as ContentItem;
};

const hydrateMatchingExercise: ExerciseHydrator = ({ base, definition }) => {
  const details = definition.exerciseDetails ?? {};
  const hydratedDetails = exerciseRegistry.hydrateDetails('Matching', details);
  const pairs = (hydratedDetails.pairs as any[]) ?? [];

  return {
    ...base,
    type: ContentType.Exercise,
    data: {
      ...createExerciseBase(base, definition),
      exerciseSubtype: ExerciseType.Matching,
      pairs: pairs.map((pair: any, index: number) => ({
        pairId: pair?.pairId,
        promptLabel: pair?.promptLabel ?? "",
        optionLabel: pair?.optionLabel ?? "",
        order: pair?.order ?? index,
      })),
    },
  } as ContentItem;
};

const hydrateScrambleExercise: ExerciseHydrator = ({ base, definition }) => {
  const details = definition.exerciseDetails ?? {};
  const sentences = Array.isArray(details.sentences) ? details.sentences : [];

  return {
    ...base,
    type: ContentType.Exercise,
    data: {
      ...createExerciseBase(base, definition),
      exerciseSubtype: ExerciseType.Scramble,
      sentences: sentences.map((sentence: any, index: number) => ({
        sentenceId: sentence?.sentenceId,
        correctSentence: sentence?.correctSentence ?? "",
        order: sentence?.order ?? index,
        correction: sentence?.correction ?? "",
      })),
    },
  } as ContentItem;
};

const hydrateFileUploadExercise: ExerciseHydrator = ({ base, definition }) => {
  return {
    ...base,
    type: ContentType.Exercise,
    data: {
      ...createExerciseBase(base, definition),
      exerciseSubtype: ExerciseType.FileUpload,
    },
  } as ContentItem;
};

// --- Hydrator Lookup Maps ---

const exerciseHydrators: Record<string, ExerciseHydrator> = {
  [ExerciseType.MultipleChoice]: hydrateMultipleChoiceExercise,
  [ExerciseType.Written]: hydrateWrittenExercise,
  [ExerciseType.LongAnswer]: hydrateWrittenExercise,
  [ExerciseType.ShortAnswer]: hydrateWrittenExercise,
  [ExerciseType.Recorded]: hydrateRecordedExercise,
  [ExerciseType.FillInBlank]: hydrateFillInBlankExercise,
  [ExerciseType.SelectForBlank]: hydrateSelectForBlankExercise,
  [ExerciseType.Conversational]: hydrateConversationalExercise,
  [ExerciseType.Matching]: hydrateMatchingExercise,
  [ExerciseType.Scramble]: hydrateScrambleExercise,
  [ExerciseType.FileUpload]: hydrateFileUploadExercise,
};

const hydrateExerciseContent: ContentHydrator = ({ base, definition }) => {
  const exerciseDefinition: ExerciseContentDefinition = definition ?? {};
  const resolvedType = (exerciseDefinition.exerciseDetails?.type
    ?? exerciseDefinition.exerciseType) as ExerciseType | undefined;
  const hydrator = (resolvedType && exerciseHydrators[resolvedType]) || hydrateFallbackExercise;
  return hydrator({ base, definition: exerciseDefinition });
};

const contentHydrators: Record<string, ContentHydrator> = {
  text: hydrateTextContent,
  image: hydrateImageContent,
  video: hydrateVideoContent,
  audio: hydrateAudioContent,
  document: hydrateDocumentContent,
  exercise: hydrateExerciseContent,
};

// --- Public API ---

export const buildContentDefinitionQuery = (
  t: any,
  request: ContentDefinitionRequest,
) => {
  const enabled = request.contentId.trim() !== "";

  switch (request.contentType) {
    case "image":
      return t.content.getImageContent({ contentId: request.contentId }, { enabled });
    case "text":
      return t.content.getTextContent({ contentId: request.contentId }, { enabled });
    case "video":
      return t.content.getVideoContent({ contentId: request.contentId }, { enabled });
    case "audio":
      return t.content.getAudioContent({ contentId: request.contentId }, { enabled });
    case "document":
      return t.content.getDocumentContent({ contentId: request.contentId }, { enabled });
    case "exercise":
      return t.content.getExerciseContent({ contentId: request.contentId }, { enabled });
    default:
      return null;
  }
};

export const hydrateContentItem = (
  rawItem: any,
  definitionsByContentId: Map<string, unknown>,
): ContentItem | null => {
  const unwrappedItem = unwrap(rawItem) as RawContentItem | undefined;
  if (!unwrappedItem?.content?.contentType) return null;

  const base = createBaseContent(unwrappedItem);
  if (!base) return null;

  const contentTypeKey = unwrappedItem.content.contentType.toLowerCase();
  const hydrator = contentHydrators[contentTypeKey];
  if (!hydrator) return null;

  const definition = definitionsByContentId.get(unwrappedItem.content.contentId);
  return hydrator({ base, definition, item: unwrappedItem });
};
